import { useState, useContext, useEffect } from "react";
import { Formik, Form as FormikForm, Field, useFormikContext } from "formik";
import { Row, Col } from "react-bootstrap";
import * as Yup from "yup";
import { UserDataContext } from "../components/UserDataContext";
import { registerLocale } from "react-datepicker";
import { addDays, isAfter, format } from "date-fns";
import "../components/button.css";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  ThemeProvider,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { pl } from "date-fns/locale";
import { marginBottom } from "../App";
import Alerts from "../components/Alerts";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { theme } from "../components/theme";
import { apiRequest } from "../components/Config";

const validationSchema = Yup.object().shape({
  nip: Yup.string().required("NIP jest wymagany"),
  user: Yup.string().required("Konto jest wymagane"),
  fullName: Yup.string().required("Dane są wymagane"),
  city: Yup.string().required("Adres firmy jest wymagany"),
  company: Yup.string().required("Nazwa firmy jest wymagana"),
  country: Yup.string().required("Kraj jest wymagany"),
  email: Yup.string()
    .email("Nieprawidłowy adres email")
    .required("Email jest wymagany"),
  selectedDate: Yup.date()
    .nullable()
    .required("Data jest wymagana")
    .test(
      "is-after-today",
      "Data musi być późniejsza niż dzisiaj",
      (value) => value && isAfter(value, new Date())
    ),
});

// 👇 komponent pomocniczy do ustawiania domyślnego kraju
function SetDefaultCountry({ country }) {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (country.length > 0 && !values.country) {
      setFieldValue("country", country[0].id);
    }
  }, [country, values.country, setFieldValue]);

  return null;
}

function clearFormButKeepCountry(values, setValues) {
  setValues({
    ...values, // zachowaj aktualne wartości
    nip: "",
    regon: "",
    user: "",
    fullName: "",
    company: "",
    city: "",
    statusVat: "",
    email: "",
    phone: "",
    permId: "",
    // zostawiamy:
    country: values.country,
    selectedDate: values.selectedDate,
    expire: values.expire,
  });
}

export default function AddUser({ perm, refreshData }) {
  registerLocale("pl", pl);

  const { userData } = useContext(UserDataContext);
  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);
  const [country, setCountry] = useState([]);
  const [error, setError] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const initialValues = {
    nip: "",
    regon: "",
    user: "",
    fullName: "",
    company: "",
    city: "",
    statusVat: "",
    country: "",
    email: "",
    phone: "",
    selectedDate: new Date(new Date().getFullYear(), 11, 31),
    expire: "",
    permId: "",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiRequest("countries");
        setCountry(response);
      } catch (error) {
        console.log(error.message);
      }
    };

    if (userData && userData.userId) {
      fetchData();
    }
  }, [userData]);

  function sanitizeAddress(input, { maxLen = 200, useWhitelist = true } = {}) {
    if (typeof input !== "string") return "";
    let s = input.replace(/[\x00-\x1F\x7F]+/g, " ");
    if (useWhitelist) {
      s = s.replace(/[^A-Za-zÀ-ž0-9\s\.\,\-\/\(\)\#]/g, " ");
    }
    s = s.replace(/\s+/g, " ").trim();
    s = s.replace(/\s+([.,])/g, "$1");
    if (s.length > maxLen) s = s.slice(0, maxLen).trim();
    return s;
  }

  function normalizeVat(vat) {
    return vat.replace(/[^A-Za-z0-9]/g, "");
  }
  const fetchGUS = async (nip, setFieldValue, values) => {
    nip = normalizeVat(nip);
    setShowApiResponse(false);
    //    console.log("Formik: ", values);
    setFieldValue("company", "");
    setFieldValue("regon", "");
    setFieldValue("statusVat", "");
    setFieldValue("city", "");
    try {
      const response = await apiRequest("mf/" + nip);
      if (response !== null) {
        setFieldValue("company", response.result.subject.name || "");
        setFieldValue("regon", response.result.subject.regon || "");
        setFieldValue("city", response.result.subject.workingAddress || "");
        setFieldValue("statusVat", response.result.subject.statusVat || "");
      } else {
        setApiResponse({
          title: "Błąd",
          severity: "error",
          info: "Brak informacji o takim NIP w Ministerstwie Finansów lub został utracony dostęp do danych!",
        });
        setShowApiResponse(true);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchVIES = async (nip, country, setFieldValue) => {
    try {
      const response = await apiRequest("vies/" + country + "/" + nip);
      setFieldValue("company", sanitizeAddress(response.name) || "");
      setFieldValue("city", sanitizeAddress(response.address) || "");
      setFieldValue("statusVat", response.isValid || "");
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchVatCheck = async (nip, country, setFieldValue) => {
    if (country === "28") {
      nip = "GB" + nip;
    }
    try {
      const response = await apiRequest("vatcheck/" + nip);
      if (response.error_msg === 501) {
        setApiResponse({
          title: "Błąd",
          severity: "error",
          info: "Taki NIP już istnieje w systemie!",
        });
        setShowApiResponse(true);
      }

      let isRegistered, name, address;
      if (response.registration_info?.is_registered) {
        isRegistered = response.registration_info.is_registered;
        name = response.registration_info.name;
        address = response.registration_info.address;
      } else if (response.registration_info_history?.length) {
        const h = response.registration_info_history[0];
        isRegistered = h.is_registered;
        name = h.name;
        address = h.address;
      } else {
        isRegistered = false;
        name = null;
        address = null;
      }
      setFieldValue("company", name);
      setFieldValue("regon", "");
      setFieldValue("city", address);
      setFieldValue("statusVat", isRegistered);
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const formatted = format(values.selectedDate, "yyyy-MM-dd");
    values["idgrp"] = userData.idGrp;
    values["expire"] = formatted;
    values["permId"] = perm;

    values.country = parseInt(values.country, 10);
    values.nip = normalizeVat(values.nip);
    //console.log("Do API: ", values);

    try {
      const response = await apiRequest("register", "POST", values);

      if (response.error_msg === 200) {
        setShowApiResponse(true);
        setApiResponse({
          title: "Sukces",
          severity: "success",
          info: "Użytkownik dodany!",
        });
        refreshData();
      }
      if (response.error_msg === 500) {
        setApiResponse({
          title: "Błąd",
          severity: "error",
          info: "Coś poszło nie tak podczas rejestracji. Prawdopodobnie nie zostały wybrane uprawnienia użytkownika.",
        });
        setShowApiResponse(true);
      }
      if (response.error_msg === 501) {
        setApiResponse({
          title: "Błąd",
          severity: "error",
          info: "Takie konto lub NIP już istnieje w systemie!",
        });
        setShowApiResponse(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {showApiResponse && (
        <Row style={{ marginBottom }}>
          <Alerts data={apiResponse} />
        </Row>
      )}
      <h4>Rejestracja konta Kontrahenta</h4>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <ThemeProvider theme={theme}>
            <FormikForm>
              <SetDefaultCountry country={country} />

              {/* NIP + GUS */}
              <Row style={{ marginBottom }} className="align-items-center">
                <Col md="3">
                  <Field name="country">
                    {({ field, form, meta }) => (
                      <FormControl
                        fullWidth
                        variant="standard"
                        error={meta.touched && Boolean(meta.error)}
                      >
                        <InputLabel id="country-label">Kraj</InputLabel>
                        <Select
                          {...field}
                          labelId="country-label"
                          name="country"
                          id="country"
                          value={field.value || ""}
                          onChange={(e) =>
                            form.setFieldValue("country", e.target.value)
                          }
                        >
                          {country.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.country}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>
                </Col>
                <Col>
                  <Field name="nip">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="NIP"
                        fullWidth
                        variant="standard"
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
                {values.country === "20" && (
                  <Col>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        fetchGUS(values.nip, setFieldValue, values)
                      }
                    >
                      <FilterAltIcon /> Pobierz z MF
                    </Button>
                  </Col>
                )}
                {values.country !== "20" && values.country !== "28" && (
                  <Col>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        fetchVIES(values.nip, values.country, setFieldValue)
                      }
                    >
                      <FilterAltIcon /> Pobierz z VIES
                    </Button>
                  </Col>
                )}
                {values.country === "28" && (
                  <Col>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        fetchVatCheck(values.nip, values.country, setFieldValue)
                      }
                    >
                      <FilterAltIcon /> Pobierz z VAT CHECK
                    </Button>
                  </Col>
                )}
              </Row>

              {/* Firma */}
              <Row style={{ marginBottom }}>
                <Col md="9">
                  <Field name="company">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Firma"
                        variant="standard"
                        fullWidth
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
                <Col md="3">
                  <Field name="statusVat">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Status VAT"
                        variant="standard"
                        fullWidth
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
              </Row>

              {/* REGON */}
              <Row style={{ marginBottom }}>
                <Col>
                  <Field name="regon">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Regon"
                        variant="standard"
                        fullWidth
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
              </Row>

              {/* Adres + Kraj */}
              <Row style={{ marginBottom }}>
                <Col md="9">
                  <Field name="city">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Adres Firmy"
                        variant="standard"
                        fullWidth
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
              </Row>

              {/* Konto, Osoba, Email, Telefon */}
              <Row style={{ marginBottom }}>
                <Col md="3">
                  <Field name="user">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Konto logowania"
                        variant="standard"
                        fullWidth
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
                <Col md="3">
                  <Field name="fullName">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Osoba kontaktowa"
                        variant="standard"
                        fullWidth
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
                <Col md="3">
                  <Field name="email">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Email"
                        variant="standard"
                        type="email"
                        fullWidth
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
                <Col md="3">
                  <Field name="phone">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Telefon"
                        variant="standard"
                        fullWidth
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Col>
              </Row>

              {/* Data ważności */}
              <Row style={{ marginBottom }}>
                <Col>
                  <Field name="selectedDate">
                    {({ field, form, meta }) => (
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={pl}
                      >
                        <DatePicker
                          label="Wybierz datę ważności konta"
                          variant="standard"
                          value={field.value}
                          onChange={(date) =>
                            form.setFieldValue(field.name, date)
                          }
                          minDate={addDays(new Date(), 1)}
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              error: meta.touched && Boolean(meta.error),
                              helperText: meta.touched && meta.error,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  </Field>
                </Col>
              </Row>

              {/* Submit */}
              <Row style={{ marginBottom }}>
                <Col>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Rejestruj
                  </Button>
                </Col>
              </Row>
            </FormikForm>
          </ThemeProvider>
        )}
      </Formik>
    </div>
  );
}
